import { Button } from "@/components/ui/button.tsx";
import { Calendar } from "@/components/ui/calendar.tsx";
import { Dialog, DialogContent } from "@/components/ui/dialog.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  type SelectTriggerVariant,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { cn } from "@/lib/utils.ts";
import {
  DAY_CODES,
  DAY_LABELS,
  type ParsedRrule,
  buildRrule,
  generatePresets,
  parseRrule,
  rruleToText,
} from "@/shared/rrule.ts";
import { format, getDay } from "date-fns";
import { Repeat } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

// --- RecurrencePicker ---

type RecurrencePickerProps = {
  value: string[] | undefined;
  onChange: (rrule: string[] | undefined) => void;
  eventDate: Date;
  disabled?: boolean;
  triggerClassName?: string;
  triggerVariant?: SelectTriggerVariant;
};

export const RecurrencePicker = ({
  value,
  onChange,
  eventDate,
  disabled,
  triggerClassName,
  triggerVariant,
}: RecurrencePickerProps) => {
  const currentRrule = value?.[0];
  const presets = useMemo(() => generatePresets(eventDate), [eventDate]);
  const [customOpen, setCustomOpen] = useState(false);
  const displayText = rruleToText(currentRrule, eventDate);

  const matchesPreset = presets.some((p) => p.rrule === currentRrule);

  const handlePresetSelect = useCallback(
    (rruleValue: string) => {
      if (rruleValue === "__none__") {
        onChange(undefined);
      } else if (rruleValue === "__custom__") {
        setCustomOpen(true);
      } else {
        onChange([rruleValue]);
      }
    },
    [onChange],
  );

  const handleCustomDone = useCallback(
    (rrule: string) => {
      onChange([rrule]);
      setCustomOpen(false);
    },
    [onChange],
  );

  return (
    <>
      <div className="flex items-center gap-3">
        <Tooltip>
          <TooltipTrigger asChild>
            <Repeat className="size-4 text-muted-foreground shrink-0" />
          </TooltipTrigger>
          <TooltipContent side="left">Recurrence</TooltipContent>
        </Tooltip>
        <Select
          value={currentRrule ?? "__none__"}
          onValueChange={handlePresetSelect}
          disabled={disabled}
        >
          <SelectTrigger
            variant={triggerVariant}
            className={cn(
              "w-auto border-0 shadow-none px-0 h-auto text-sm text-muted-foreground hover:text-foreground focus:ring-0",
              triggerClassName,
            )}
          >
            <SelectValue>{!matchesPreset && currentRrule ? displayText : undefined}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {presets.map((p) => (
              <SelectItem key={p.rrule ?? "__none__"} value={p.rrule ?? "__none__"}>
                {p.label}
              </SelectItem>
            ))}
            <SelectItem value="__custom__">Custom...</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {customOpen && (
        <CustomRecurrenceDialog
          open={customOpen}
          onClose={() => setCustomOpen(false)}
          onDone={handleCustomDone}
          initialRrule={currentRrule}
          eventDate={eventDate}
        />
      )}
    </>
  );
};

// --- Custom Recurrence Dialog ---

const FREQ_OPTIONS = [
  { value: "daily", label: "day" },
  { value: "weekly", label: "week" },
  { value: "monthly", label: "month" },
  { value: "yearly", label: "year" },
];

const END_TYPE_OPTIONS = [
  { value: "never", label: "Never" },
  { value: "on", label: "On" },
  { value: "after", label: "After" },
] as const;

const CustomRecurrenceDialog = ({
  open,
  onClose,
  onDone,
  initialRrule,
  eventDate,
}: {
  open: boolean;
  onClose: () => void;
  onDone: (rrule: string) => void;
  initialRrule?: string;
  eventDate: Date;
}) => {
  const initial = useMemo(() => {
    if (initialRrule) return parseRrule(initialRrule);
    const dayCode = DAY_CODES[getDay(eventDate)];
    return { freq: "weekly" as const, interval: 1, days: [dayCode], endType: "never" as const };
  }, [initialRrule, eventDate]);

  const [freq, setFreq] = useState<ParsedRrule["freq"]>(initial.freq);
  const [interval, setInterval] = useState(initial.interval);
  const [days, setDays] = useState<string[]>(initial.days);
  const [endType, setEndType] = useState<ParsedRrule["endType"]>(initial.endType);
  const [until, setUntil] = useState<Date | undefined>(initial.until);
  const [count, setCount] = useState(initial.count ?? 4);

  const toggleDay = (day: string) => {
    setDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  const handleDone = () => {
    onDone(buildRrule({ freq, interval, days, endType, until, count }));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Repeat</h3>

          {/* Interval + Frequency */}
          <div className="flex items-center gap-2">
            <Label className="text-sm">Every</Label>
            <Input
              type="number"
              min={1}
              max={99}
              value={interval}
              onChange={(e) => setInterval(Math.max(1, Number.parseInt(e.target.value, 10) || 1))}
              className="w-16 text-center"
            />
            <Select value={freq} onValueChange={(v) => setFreq(v as ParsedRrule["freq"])}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQ_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Day selector (weekly only) */}
          {freq === "weekly" && (
            <div className="space-y-1.5">
              <Label>On</Label>
              <div className="flex gap-1">
                {DAY_CODES.map((code, i) => (
                  <Button
                    key={code}
                    type="button"
                    variant={days.includes(code) ? "default" : "outline"}
                    size="icon"
                    data-statsig-name={`recurrence-day-${code}`}
                    onClick={() => toggleDay(code)}
                    className={cn("size-8 rounded-full text-xs")}
                  >
                    {DAY_LABELS[i]}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Ends */}
          <div className="space-y-2">
            <Label>Ends</Label>
            {END_TYPE_OPTIONS.map((opt) => (
              <div key={opt.value} className="flex items-center gap-2">
                <Button
                  type="button"
                  variant={endType === opt.value ? "default" : "outline"}
                  size="sm"
                  data-statsig-name={`recurrence-end-${opt.value}`}
                  onClick={() => setEndType(opt.value)}
                  className="min-w-16"
                >
                  {opt.label}
                </Button>
                {opt.value === "on" && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        data-statsig-name="recurrence-until-date"
                        disabled={endType !== "on"}
                      >
                        {until ? format(until, "MMM d, yyyy") : "Pick date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={until}
                        onSelect={(day) => day && setUntil(day)}
                        disabled={{ before: eventDate }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                )}
                {opt.value === "after" && (
                  <>
                    <Input
                      type="number"
                      min={1}
                      max={999}
                      value={count}
                      onChange={(e) =>
                        setCount(Math.max(1, Number.parseInt(e.target.value, 10) || 1))
                      }
                      disabled={endType !== "after"}
                      className="w-16 text-center"
                    />
                    <span className="text-sm text-muted-foreground">times</span>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 justify-end">
            <Button
              variant="outline"
              data-statsig-name="recurrence-custom-cancel"
              onClick={onClose}
              type="button"
            >
              Cancel
            </Button>
            <Button data-statsig-name="recurrence-custom-done" onClick={handleDone} type="button">
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
